import { forwardRef, useCallback, useId } from 'react'
import type { ForwardedRef } from 'react'
import { Slider } from 'radix-ui'
import classNames from 'classnames'
import type { FormFieldDefaults } from './types'
import { FormError } from './form/FormError'

interface RangeSliderProps extends Omit<
  FormFieldDefaults<'input'>,
  'value' | 'onChange'
> {
  min: number
  max: number
  step?: number
  value: [number, number]
  onChange: (next: [number, number]) => void

  /**
   * Soft clamp for the lower thumb. Used when the visible track must show the
   * full range but a domain rule forbids dragging below this value (e.g. roles
   * impose a minimum group size). Defaults to `min`.
   */
  effectiveMin?: number
  /**
   * Soft clamp for the upper thumb. Defaults to `max`.
   */
  effectiveMax?: number

  /**
   * Pill labels above each thumb. When the two thumbs land on the same value
   * the lower pill is hidden, so make `maxLabelFormatter` return whatever
   * single-value copy you want shown in that case.
   */
  minLabelFormatter?: (n: number) => string
  maxLabelFormatter?: (n: number) => string

  minThumbAriaLabel: string
  maxThumbAriaLabel: string

  /** Caption rendered below the track and wired to `aria-describedby`. */
  description?: string

  /**
   * Visible explanation rendered on its own line below the description when
   * `disabled` is true. Used to communicate *why* the slider is read-only.
   * Wired into `aria-describedby` alongside the description, so screen
   * readers announce the reason as part of the slider's accessible
   * description. Radix also wires `aria-disabled` on each thumb.
   */
  readOnlyLabel?: string
}

const defaultFormatter = (n: number) => String(n)

/**
 * Dual-thumb range slider built on Radix's accessible Slider primitive.
 * Thumbs render with floating pill labels above the track. The visible track
 * always spans `min`..`max`; `effectiveMin`/`effectiveMax` constrain the
 * thumbs without shrinking the track, and per-thumb `aria-valuemin`/
 * `aria-valuemax` communicate the active bounds to assistive tech. A tick
 * mark is rendered at every integer step so the scale is visible.
 */
export const BreakoutRangeSlider = forwardRef(function BreakoutRangeSlider(
  {
    disabled,
    name,
    className,
    testId,
    value,
    error,
    errorClass,
    onChange,
    min,
    max,
    step = 1,
    effectiveMin,
    effectiveMax,
    minLabelFormatter = defaultFormatter,
    maxLabelFormatter = defaultFormatter,
    minThumbAriaLabel,
    maxThumbAriaLabel,
    description,
    readOnlyLabel,
  }: RangeSliderProps,
  ref: ForwardedRef<HTMLSpanElement>
) {
  const reactId = useId()
  const descriptionId = description ? `${reactId}-desc` : undefined
  const reasonId = disabled && readOnlyLabel ? `${reactId}-reason` : undefined
  const ariaDescribedBy =
    [descriptionId, reasonId].filter(Boolean).join(' ') || undefined

  const rootTestId = testId || 'range-slider'

  const eMin = effectiveMin ?? min
  const eMax = effectiveMax ?? max
  const [lo, hi] = value

  const minPillLabel = lo === hi ? null : minLabelFormatter(lo)
  const maxPillLabel = maxLabelFormatter(hi)

  const onValueChange = useCallback(
    (next: number[]) => {
      let nextLo = next[0] ?? lo
      let nextHi = next[1] ?? hi
      if (nextLo < eMin) nextLo = eMin
      if (nextHi > eMax) nextHi = eMax
      if (nextLo > nextHi) nextLo = nextHi
      if (nextLo === lo && nextHi === hi) return
      onChange([nextLo, nextHi])
    },
    [onChange, lo, hi, eMin, eMax]
  )

  const ticks: number[] = []
  if (step > 0 && max > min) {
    for (let i = min; i <= max; i += step) ticks.push(i)
  }

  return (
    <div className={classNames('flex flex-col gap-2', className)}>
      {/* Spacer so the floating pill labels above each thumb don't collide
          with surrounding form rows. */}
      <div className="h-9" />
      <Slider.Root
        ref={ref}
        className={classNames(
          'relative flex h-5 w-full touch-none select-none items-center',
          {
            'cursor-not-allowed text-on-surface-disabled': disabled,
          }
        )}
        value={[lo, hi]}
        onValueChange={onValueChange}
        min={min}
        max={max}
        step={step}
        minStepsBetweenThumbs={0}
        name={name}
        disabled={disabled}
        data-testid={rootTestId}
        aria-describedby={ariaDescribedBy}
      >
        <Slider.Track className="relative h-1 grow rounded-lg bg-outline-medium">
          <Slider.Range
            className={classNames(
              'absolute h-1 rounded-lg',
              disabled ? 'bg-on-surface-disabled' : 'bg-fixed-accent-color'
            )}
          />
          {ticks.map((tick) => {
            // Radix insets each thumb by half its width at the ends so it
            // doesn't overhang the track. Use the same inset for ticks so
            // a tick at `tick === min` lines up with the thumb at min, and
            // a tick at `tick === max` lines up with the thumb at max.
            // Thumb is `size-4` (16px) — half-width is 8px.
            const fraction = (tick - min) / (max - min)
            const inRange = tick >= lo && tick <= hi
            const inRangeColor = disabled
              ? 'bg-on-surface-disabled'
              : 'bg-fixed-accent-color'
            return (
              <div
                key={tick}
                aria-hidden="true"
                className={classNames(
                  'pointer-events-none absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full',
                  inRange ? inRangeColor : 'bg-outline-medium'
                )}
                style={{ left: `calc(8px + (100% - 16px) * ${fraction})` }}
              />
            )
          })}
        </Slider.Track>
        <Slider.Thumb
          data-testid={`${rootTestId}-min-thumb`}
          className={classNames(
            'relative block size-4 rounded-full border ring-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-on-surface-disabled',
            disabled
              ? 'cursor-not-allowed border-on-surface-disabled bg-on-surface-disabled'
              : 'border-fixed-accent-color bg-fixed-accent-color'
          )}
          aria-label={minThumbAriaLabel}
          aria-valuemin={eMin}
          aria-valuemax={hi}
          aria-valuetext={minLabelFormatter(lo)}
        >
          {minPillLabel && (
            <ThumbPill label={minPillLabel} disabled={disabled} />
          )}
        </Slider.Thumb>
        <Slider.Thumb
          data-testid={`${rootTestId}-max-thumb`}
          className={classNames(
            'relative block size-4 rounded-full border ring-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-on-surface-disabled',
            disabled
              ? 'cursor-not-allowed border-on-surface-disabled bg-on-surface-disabled'
              : 'border-fixed-accent-color bg-fixed-accent-color'
          )}
          aria-label={maxThumbAriaLabel}
          aria-valuemin={lo}
          aria-valuemax={eMax}
          aria-valuetext={maxLabelFormatter(hi)}
        >
          <ThumbPill label={maxPillLabel} disabled={disabled} />
        </Slider.Thumb>
      </Slider.Root>
      {/* Number labels under each tick. Same horizontal math as the ticks. */}
      <div className="relative -mt-1 h-4 w-full" aria-hidden="true">
        {ticks.map((tick) => {
          const fraction = (tick - min) / (max - min)
          return (
            <div
              key={tick}
              className={classNames(
                'absolute top-0 -translate-x-1/2 text-label-small',
                disabled ? 'text-on-surface-disabled' : 'text-on-surface-var'
              )}
              style={{ left: `calc(8px + (100% - 16px) * ${fraction})` }}
            >
              {tick}
            </div>
          )
        })}
      </div>
      {description && (
        <p
          id={descriptionId}
          className="text-body-small text-on-surface-disabled"
        >
          {description}
        </p>
      )}
      {disabled && readOnlyLabel && (
        <p
          id={reasonId}
          data-testid={`${rootTestId}-read-only-reason`}
          className="text-body-small font-bold text-on-surface-disabled"
        >
          {readOnlyLabel}
        </p>
      )}
      <FormError error={error} errorClass={errorClass} />
    </div>
  )
})

const ThumbPill = ({
  label,
  disabled,
}: {
  label: string
  disabled?: boolean
}) => (
  // Pointer events are intentionally enabled so a click+drag on the pill
  // bubbles to the parent Slider.Thumb and counts as grabbing the thumb.
  <div
    aria-hidden="true"
    className={classNames(
      'absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-outline-light bg-surface px-3 py-1 text-label-medium text-on-surface shadow-sm',
      disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'
    )}
  >
    {label}
  </div>
)
